import { z } from 'zod'
import { startOfDay, subDays, endOfDay, parseISO, isValid } from 'date-fns'
import type { IncidentListParams } from '@/entities/incident'

export const incidentSearchSchema = z.object({
  view: z.enum(['board', 'list']).optional(),
  q: z.coerce.string().optional(),
  clientId: z.coerce.number().int().positive().optional(),
  siteId: z.coerce.number().int().positive().optional(),
  range: z.enum(['today', '7d', '30d', 'all', 'custom']).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  status: z.enum(['Open', 'UnderReview', 'Resolved']).optional(),
  severity: z.enum(['Low', 'Medium', 'High', 'Critical']).optional(),
  categoryId: z.coerce.number().int().positive().optional(),
  subtypeId: z.coerce.number().int().positive().optional(),
  guardUserId: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().positive().optional(),
})

export type IncidentFilters = z.infer<typeof incidentSearchSchema>

export interface IncidentFilterLocks {
  clientId?: number
  guardUserId?: number
}

const NUMBER_SHAPE = /^\s*(?:inc)?[\s#-]*0*(\d{1,12})\s*$/i

export function isNumberSearch(q: string | undefined): boolean {
  return !!q && NUMBER_SHAPE.test(q)
}

export function searchedReference(q: string | undefined): string | undefined {
  const digits = q ? NUMBER_SHAPE.exec(q)?.[1] : undefined
  return digits ? `INC-${digits.padStart(4, '0')}` : undefined
}

export function hasActiveFilters(filters: IncidentFilters, locks: IncidentFilterLocks = {}): boolean {
  return Boolean(
    filters.q || (locks.clientId === undefined && filters.clientId) || filters.siteId || filters.status ||
    filters.severity || filters.categoryId || filters.subtypeId ||
    (locks.guardUserId === undefined && filters.guardUserId) ||
    (filters.range && filters.range !== '30d'),
  )
}

function parseDay(value: string | undefined): Date | undefined {
  if (!value) return undefined
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : undefined
}

function dateWindow(filters: IncidentFilters): { fromDate?: string; toDate?: string } {
  const now = new Date()
  switch (filters.range ?? '30d') {
    case 'today':
      return { fromDate: startOfDay(now).toISOString() }
    case '7d':
      return { fromDate: startOfDay(subDays(now, 7)).toISOString() }
    case '30d':
      return { fromDate: startOfDay(subDays(now, 30)).toISOString() }
    case 'custom': {
      const from = parseDay(filters.from)
      const to = parseDay(filters.to)
      return {
        fromDate: from ? startOfDay(from).toISOString() : undefined,
        toDate: to ? endOfDay(to).toISOString() : undefined,
      }
    }
    default:
      return {}
  }
}

export const LIST_PAGE_SIZE = 25

export function toListParams(filters: IncidentFilters, locks: IncidentFilterLocks = {}): IncidentListParams {
  const clientId = locks.clientId ?? filters.clientId
  const guardUserId = locks.guardUserId ?? filters.guardUserId
  if (isNumberSearch(filters.q)) {
    return { search: filters.q, clientId: locks.clientId, guardUserId: locks.guardUserId, pageSize: LIST_PAGE_SIZE }
  }
  return {
    search: filters.q && filters.q.trim().length >= 3 ? filters.q.trim() : undefined,
    clientId,
    siteId: filters.siteId,
    statusFilter: filters.status,
    severityFilter: filters.severity,
    categoryId: filters.categoryId,
    subtypeId: filters.subtypeId,
    guardUserId,
    ...dateWindow(filters),
    sort: 'occurred',
    pageNumber: filters.page ?? 1,
    pageSize: LIST_PAGE_SIZE,
  }
}

export function toBoardParams(filters: IncidentFilters, locks: IncidentFilterLocks = {}): IncidentListParams {
  return {
    clientId: locks.clientId ?? filters.clientId,
    siteId: filters.siteId,
    severityFilter: filters.severity,
    categoryId: filters.categoryId,
    subtypeId: filters.subtypeId,
    guardUserId: locks.guardUserId ?? filters.guardUserId,
  }
}
